export class LocalStorageService<TKey extends string = string> {
  private keyPrefix: string = '';

  constructor(keyPrefix?: string) {
    this.keyPrefix = keyPrefix ? `${keyPrefix}_` : '';
  }

  /**
   * Store a value in localStorage with type safety
   * @param key The storage key
   * @param value The value to store
   */
  set<T>(key: TKey, value: T): void {
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(this.getKey(key), serializedValue);
    } catch (error) {
      console.error('Error storing item in localStorage:', error);
      throw new Error(`Failed to store item with key: ${key}`);
    }
  }

  /**
   * Retrieve a value from localStorage with type safety
   * @param key The storage key
   * @param defaultValue Optional default value if key doesn't exist
   * @returns The retrieved value of type T or defaultValue
   */
  get<T>(key: TKey, defaultValue?: T): T | null {
    try {
      const item = localStorage.getItem(this.getKey(key));

      if (item === null) {
        return defaultValue ?? null;
      }

      return JSON.parse(item) as T;
    } catch (error) {
      console.error('Error retrieving item from localStorage:', error);
      return defaultValue ?? null;
    }
  }

  /**
   * Retrieve a value from localStorage, throwing if not found
   * @param key The storage key
   * @returns The retrieved value of type T
   * @throws Error if the key doesn't exist
   */
  getRequired<T>(key: TKey): T {
    const value = this.get<T>(key);

    if (value === null) {
      throw new Error(`Required localStorage item not found: ${key}`);
    }

    return value;
  }

  /**
   * Check if a key exists in localStorage
   * @param key The storage key
   * @returns true if the key exists
   */
  has(key: TKey): boolean {
    return localStorage.getItem(this.getKey(key)) !== null;
  }

  /**
   * Remove an item from localStorage
   * @param key The storage key
   */
  remove(key: TKey): void {
    localStorage.removeItem(this.getKey(key));
  }

  /**
   * Clear all items with the current prefix from localStorage
   */
  clear(): void {
    if (this.keyPrefix) {
      // Remove only items with our prefix
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.keyPrefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } else {
      // Clear all localStorage
      localStorage.clear();
    }
  }

  /**
   * Get all keys with the current prefix
   * @returns Array of keys (without prefix)
   */
  keys(): string[] {
    const keys: string[] = [];
    const prefixLength = this.keyPrefix.length;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.keyPrefix)) {
        keys.push(key.substring(prefixLength));
      }
    }

    return keys;
  }

  /**
   * Get the size of localStorage data for this service
   * @returns Object with count and estimated size in bytes
   */
  getStorageInfo(): { count: number; estimatedSizeBytes: number } {
    let count = 0;
    let estimatedSizeBytes = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.keyPrefix)) {
        count++;
        const value = localStorage.getItem(key);
        if (value) {
          // Rough estimation: 2 bytes per character (UTF-16)
          estimatedSizeBytes += (key.length + value.length) * 2;
        }
      }
    }

    return { count, estimatedSizeBytes };
  }

  /**
   * Update an existing item or create if it doesn't exist
   * @param key The storage key
   * @param updateFn Function that receives current value and returns updated value
   * @param defaultValue Default value if key doesn't exist
   */
  update<T>(key: TKey, updateFn: (current: T | null) => T, defaultValue?: T): void {
    const current = this.get<T>(key, defaultValue);
    const updated = updateFn(current);
    this.set(key, updated);
  }

  /**
   * Get multiple items at once
   * @param keys Array of storage keys
   * @returns Object with key-value pairs
   */
  getMultiple<T>(keys: TKey[]): Record<string, T | null> {
    const result: Record<string, T | null> = {};

    keys.forEach((key) => {
      result[key] = this.get<T>(key);
    });

    return result;
  }

  /**
   * Set multiple items at once
   * @param items Object with key-value pairs
   */
  setMultiple<T>(items: Record<TKey, T>): void {
    Object.entries(items).forEach(([key, value]) => {
      this.set(key as TKey, value);
    });
  }

  private getKey(key: TKey): string {
    return `${this.keyPrefix}${key}`;
  }
}
